/**
 * The whole editor as one pure reducer.
 *
 * Actions are interaction-level (pointer down/move/up) with the pointer state
 * machine living here rather than in a component, so the workflow test exercises
 * the same code path the mouse does and there is no geometry in the React tree.
 */

import type { MeasureText } from '../geometry/drawable';
import { polylinesOf } from '../geometry/cache';
import {
  pickElement,
  pickHandle,
  elementsInRect,
  selectionBounds,
  type ResizeHandle,
} from '../geometry/hit';
import { moveLinearPoint, resizeElement, rotateElement, rotationGrabOffset } from '../geometry/resize';
import { rectFromPoints, toLocal, type Rect, type Vec } from '../geometry/transform';
import {
  DEFAULT_STYLE,
  createArrow,
  createBox,
  createFreedraw,
  createText,
  duplicateElement,
  updateElement,
  type EditMeta,
  type ElementStyle,
} from './elements';
import {
  EMPTY_HISTORY,
  diffChangedIds,
  pushEntry,
  restoreChanged,
  type History,
} from './history';
import { boundTextMap, normalizeScene } from './normalize';
import type { ArrowElement, Board, BoardView, Point, SlateElement, TextElement } from './types';
import { DOC_VERSION, isArrow, isBindable, isBox, isLinear, isText } from './types';
import { DEFAULT_VIEW, centerOn, clampZoom, zoomAt, zoomToFit } from './view';

export type Tool =
  | 'select'
  | 'rect'
  | 'ellipse'
  | 'diamond'
  | 'arrow'
  | 'line'
  | 'freedraw'
  | 'text'
  | 'eraser'
  | 'pan';

export const DEFAULT_BINDING_GAP = 4;
/** Click-without-drag places a shape this size. */
export const CLICK_PLACE_SIZE = { width: 140, height: 90 };
const DRAG_THRESHOLD = 3;

type Interaction =
  | { readonly kind: 'draw'; readonly id: string; readonly origin: Vec }
  | { readonly kind: 'freedraw'; readonly id: string }
  | { readonly kind: 'arrow'; readonly id: string; readonly origin: Vec }
  | {
      readonly kind: 'move';
      readonly startPointer: Vec;
      readonly ids: readonly string[];
      readonly origins: ReadonlyMap<string, { x: number; y: number }>;
      readonly moved: boolean;
    }
  | { readonly kind: 'resize'; readonly id: string; readonly handle: ResizeHandle }
  | { readonly kind: 'rotate'; readonly id: string; readonly grabOffset: number }
  | { readonly kind: 'point'; readonly id: string; readonly index: number }
  | { readonly kind: 'marquee'; readonly origin: Vec; readonly base: readonly string[] }
  | { readonly kind: 'pan'; readonly startPointer: Vec; readonly startScroll: Vec }
  | { readonly kind: 'erase' };

export interface EditorState {
  readonly boardId: string | null;
  readonly boardName: string;
  readonly isSample: boolean;
  readonly elements: readonly SlateElement[];
  readonly selectedIds: readonly string[];
  readonly tool: Tool;
  readonly lockTool: boolean;
  readonly view: BoardView;
  readonly style: ElementStyle;
  readonly editingTextId: string | null;
  readonly interaction: Interaction | null;
  readonly marquee: Rect | null;
  readonly hoverBindTargetId: string | null;
  readonly history: History;
  readonly announcement: string;
  /** What changed last, so the sync and autosave effects know what to send. */
  readonly lastChange: {
    readonly ids: readonly string[];
    readonly origin: 'local' | 'remote' | 'load';
    readonly seq: number;
  };
}

export const INITIAL_STATE: EditorState = {
  boardId: null,
  boardName: 'Untitled board',
  isSample: false,
  elements: [],
  selectedIds: [],
  tool: 'select',
  lockTool: false,
  view: DEFAULT_VIEW,
  style: DEFAULT_STYLE,
  editingTextId: null,
  interaction: null,
  marquee: null,
  hoverBindTargetId: null,
  history: EMPTY_HISTORY,
  announcement: '',
  lastChange: { ids: [], origin: 'load', seq: 0 },
};

export type Action =
  | { readonly t: 'setTool'; readonly tool: Tool }
  | { readonly t: 'toggleToolLock' }
  | { readonly t: 'pointerDown'; readonly world: Vec; readonly shift: boolean; readonly alt: boolean }
  | { readonly t: 'pointerMove'; readonly world: Vec; readonly shift: boolean }
  | { readonly t: 'pointerUp'; readonly world: Vec }
  | { readonly t: 'pointerCancel' }
  | { readonly t: 'doubleClick'; readonly world: Vec }
  | { readonly t: 'select'; readonly ids: readonly string[] }
  | { readonly t: 'selectAll' }
  | { readonly t: 'deleteSelected' }
  | { readonly t: 'nudge'; readonly dx: number; readonly dy: number }
  | { readonly t: 'setStyle'; readonly patch: Partial<ElementStyle> }
  | { readonly t: 'undo' }
  | { readonly t: 'redo' }
  | { readonly t: 'duplicate' }
  | { readonly t: 'pasteElements'; readonly elements: readonly SlateElement[]; readonly offset?: number }
  | { readonly t: 'zOrder'; readonly dir: 'front' | 'back' }
  | { readonly t: 'setView'; readonly view: BoardView }
  | { readonly t: 'zoomBy'; readonly factor: number; readonly anchor: Vec }
  | { readonly t: 'zoomReset' }
  | { readonly t: 'zoomToFit'; readonly viewport: { width: number; height: number } }
  | { readonly t: 'scrollToSelection'; readonly viewport: { width: number; height: number } }
  | { readonly t: 'startEditText'; readonly id: string }
  | { readonly t: 'editTextValue'; readonly id: string; readonly text: string }
  | { readonly t: 'endEditText' }
  | { readonly t: 'loadBoard'; readonly board: Board }
  | { readonly t: 'renameBoard'; readonly name: string }
  | { readonly t: 'applyRemote'; readonly elements: readonly SlateElement[] }
  | { readonly t: 'announce'; readonly message: string };

export interface ReducerContext {
  readonly measureText: MeasureText;
  readonly clientId: string;
  readonly now: () => number;
}

function metaOf(ctx: ReducerContext): EditMeta {
  return { clientId: ctx.clientId, now: ctx.now() };
}

function byId(elements: readonly SlateElement[]): Map<string, SlateElement> {
  return new Map(elements.map((el) => [el.id, el] as const));
}

function replaceElements(
  elements: readonly SlateElement[],
  patches: ReadonlyMap<string, SlateElement>,
): readonly SlateElement[] {
  if (patches.size === 0) return elements;
  return elements.map((el) => patches.get(el.id) ?? el);
}

function liveElements(elements: readonly SlateElement[]): SlateElement[] {
  return elements.filter((el) => !el.isDeleted);
}

/** Topmost element an arrow may attach to, ignoring the arrow being drawn. */
function pickBindTarget(
  elements: readonly SlateElement[],
  world: Vec,
  measure: MeasureText,
  excludeId: string | null,
): SlateElement | null {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i]!;
    if (el.id === excludeId || !isBindable(el)) continue;
    const local = toLocal(world, el);
    for (const poly of polylinesOf(el, measure)) {
      // Interior test rather than the stroke test used for selection: dropping
      // anywhere on a shape should connect, not just on its outline. Works for
      // a transparent shape too, which selection deliberately does not.
      if (poly.closed && pointInside(poly.points, local)) return el;
    }
  }
  return null;
}

function pointInside(points: readonly Vec[], p: Vec): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!;
    const b = points[j]!;
    if (a.y > p.y !== b.y > p.y) {
      const t = (p.y - a.y) / (b.y - a.y);
      if (p.x < a.x + t * (b.x - a.x)) inside = !inside;
    }
  }
  return inside;
}

/** Moving a container must move its label; moving a shape re-routes its arrows. */
function expandForMove(elements: readonly SlateElement[], ids: readonly string[]): string[] {
  const set = new Set(ids);
  const labels = boundTextMap(elements);
  for (const id of ids) {
    const label = labels.get(id);
    if (label) set.add(label.id);
  }
  return [...set];
}

function normalized(
  ctx: ReducerContext,
  elements: readonly SlateElement[],
  dirty: Iterable<string> | undefined,
  meta: EditMeta,
  bumpVersions = true,
): readonly SlateElement[] {
  return normalizeScene(elements, {
    dirty: dirty ? new Set(dirty) : undefined,
    measureText: ctx.measureText,
    meta,
    bumpVersions,
  });
}

/** Record a discrete (non-gesture) change in history. */
function commitNow(state: EditorState, next: EditorState): EditorState {
  const changedIds = diffChangedIds(state.elements, next.elements);
  if (changedIds.length === 0) return next;
  return {
    ...next,
    history: pushEntry(next.history, {
      elements: state.elements,
      selectedIds: state.selectedIds,
      changedIds,
    }),
    lastChange: { ids: changedIds, origin: 'local', seq: state.lastChange.seq + 1 },
  };
}

function markLocal(state: EditorState, next: EditorState): EditorState {
  const changedIds = diffChangedIds(state.elements, next.elements);
  if (changedIds.length === 0) return next;
  return { ...next, lastChange: { ids: changedIds, origin: 'local', seq: state.lastChange.seq + 1 } };
}

function beginGesture(state: EditorState): History {
  return { ...state.history, pending: { elements: state.elements, selectedIds: state.selectedIds } };
}

function commitGesture(state: EditorState): EditorState {
  const pending = state.history.pending;
  if (!pending) return state;
  const changedIds = diffChangedIds(pending.elements, state.elements);
  // A click that moved nothing must not create an undo entry.
  if (changedIds.length === 0) {
    return { ...state, history: { ...state.history, pending: null } };
  }
  return {
    ...state,
    history: pushEntry(state.history, {
      elements: pending.elements,
      selectedIds: pending.selectedIds,
      changedIds,
    }),
  };
}

function describeSelection(state: EditorState): string {
  const n = state.selectedIds.length;
  if (n === 0) return 'Nothing selected';
  if (n === 1) {
    const el = state.elements.find((e) => e.id === state.selectedIds[0]);
    if (!el) return 'Nothing selected';
    const label = isText(el) ? `text "${el.text.slice(0, 24)}"` : el.type;
    return `Selected ${label}`;
  }
  return `Selected ${n} elements`;
}

export function makeReducer(ctx: ReducerContext) {
  return function reducer(state: EditorState, action: Action): EditorState {
    switch (action.t) {
      case 'setTool': {
        const tool = action.tool;
        return {
          ...state,
          tool,
          selectedIds: tool === 'select' ? state.selectedIds : [],
          marquee: null,
          announcement: `${tool} tool`,
        };
      }

      case 'toggleToolLock':
        return { ...state, lockTool: !state.lockTool };

      case 'select': {
        const next = { ...state, selectedIds: action.ids };
        return { ...next, announcement: describeSelection(next) };
      }

      case 'selectAll': {
        const ids = liveElements(state.elements)
          .filter((el) => !(isText(el) && el.containerId))
          .map((el) => el.id);
        return { ...state, selectedIds: ids, announcement: `Selected ${ids.length} elements` };
      }

      case 'announce':
        return { ...state, announcement: action.message };

      case 'renameBoard':
        return { ...state, boardName: action.name };

      case 'setView':
        return { ...state, view: { ...action.view, zoom: clampZoom(action.view.zoom) } };

      case 'zoomBy':
        return { ...state, view: zoomAt(state.view, state.view.zoom * action.factor, action.anchor) };

      case 'zoomReset':
        return { ...state, view: { ...state.view, zoom: 1 }, announcement: 'Zoom reset to 100%' };

      case 'zoomToFit': {
        const view = zoomToFit(liveElements(state.elements), action.viewport);
        return { ...state, view, announcement: 'Zoomed to fit' };
      }

      case 'scrollToSelection': {
        const selected = state.elements.filter((el) => state.selectedIds.includes(el.id));
        const bounds = selectionBounds(selected);
        if (!bounds) return state;
        return { ...state, view: centerOn(state.view, bounds, action.viewport) };
      }

      case 'loadBoard': {
        const board = action.board;
        // A full pass on load: sample and imported boards arrive with geometry
        // that was measured elsewhere, so labels and arrow endpoints are settled
        // here rather than on the user's first click.
        const elements = normalized(ctx, board.elements, undefined, metaOf(ctx));
        return {
          ...INITIAL_STATE,
          boardId: board.id,
          boardName: board.name,
          isSample: board.isSample,
          elements,
          view: board.view,
          style: state.style,
          lockTool: state.lockTool,
          announcement: `Opened ${board.name}`,
          lastChange: { ids: [], origin: 'load', seq: state.lastChange.seq + 1 },
        };
      }

      case 'setStyle': {
        const style = { ...state.style, ...action.patch };
        const patches = new Map<string, SlateElement>();
        const meta = metaOf(ctx);
        const labels = boundTextMap(state.elements);
        for (const id of state.selectedIds) {
          const el = state.elements.find((e) => e.id === id);
          if (!el) continue;
          const patch: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(action.patch)) {
            if (value === undefined) continue;
            if (key === 'fontSize' || key === 'fontFamily' || key === 'textAlign') {
              if (!isText(el)) continue;
            }
            if (key === 'backgroundColor') {
              patch.fillStyle = value === 'transparent' ? 'transparent' : 'solid';
            }
            patch[key] = value;
          }
          if (Object.keys(patch).length === 0) continue;
          patches.set(id, updateElement(el, patch as Partial<SlateElement>, meta));
          // Styling a container should restyle its label's text too.
          const label = labels.get(id);
          if (label && (action.patch.fontSize !== undefined || action.patch.fontFamily !== undefined)) {
            patches.set(
              label.id,
              updateElement(
                label,
                {
                  fontSize: action.patch.fontSize ?? label.fontSize,
                  fontFamily: action.patch.fontFamily ?? label.fontFamily,
                } as Partial<TextElement>,
                meta,
              ),
            );
          }
        }
        const elements = normalized(
          ctx,
          replaceElements(state.elements, patches),
          [...patches.keys()],
          meta,
        );
        return commitNow(state, { ...state, style, elements });
      }

      case 'deleteSelected': {
        if (state.selectedIds.length === 0) return state;
        const meta = metaOf(ctx);
        const ids = new Set(expandForMove(state.elements, state.selectedIds));
        const patches = new Map<string, SlateElement>();
        for (const el of state.elements) {
          if (!ids.has(el.id) || el.isDeleted) continue;
          patches.set(el.id, updateElement(el, { isDeleted: true } as Partial<SlateElement>, meta));
        }
        if (patches.size === 0) return state;
        // Bindings are deliberately NOT cleared here: the resolver ignores dead
        // targets, so undo can bring the connection back intact.
        const elements = normalized(ctx, replaceElements(state.elements, patches), [...ids], meta);
        return commitNow(state, {
          ...state,
          elements,
          selectedIds: [],
          announcement: `Deleted ${patches.size} element${patches.size === 1 ? '' : 's'}`,
        });
      }

      case 'nudge': {
        if (state.selectedIds.length === 0) return state;
        const meta = metaOf(ctx);
        const ids = expandForMove(state.elements, state.selectedIds);
        const patches = new Map<string, SlateElement>();
        for (const id of ids) {
          const el = state.elements.find((e) => e.id === id);
          if (!el) continue;
          patches.set(id, updateElement(el, { x: el.x + action.dx, y: el.y + action.dy } as Partial<SlateElement>, meta));
        }
        const elements = normalized(ctx, replaceElements(state.elements, patches), ids, meta);
        return commitNow(state, { ...state, elements });
      }

      case 'duplicate': {
        if (state.selectedIds.length === 0) return state;
        const meta = metaOf(ctx);
        const copies = cloneSelection(state, meta, 16);
        if (copies.length === 0) return state;
        const elements = normalized(
          ctx,
          [...state.elements, ...copies],
          copies.map((c) => c.id),
          meta,
        );
        return commitNow(state, {
          ...state,
          elements,
          selectedIds: copies.filter((c) => !(isText(c) && c.containerId)).map((c) => c.id),
          announcement: `Duplicated ${copies.length} element${copies.length === 1 ? '' : 's'}`,
        });
      }

      case 'pasteElements': {
        const meta = metaOf(ctx);
        const offset = action.offset ?? 16;
        const idMap = new Map<string, string>();
        const copies = action.elements.map((el) => duplicateElement(el, { dx: offset, dy: offset }, meta, idMap));
        const remapped = copies.map((el) => remapReferences(el, idMap));
        if (remapped.length === 0) return state;
        const elements = normalized(
          ctx,
          [...state.elements, ...remapped],
          remapped.map((c) => c.id),
          meta,
        );
        return commitNow(state, {
          ...state,
          elements,
          selectedIds: remapped.filter((c) => !(isText(c) && c.containerId)).map((c) => c.id),
          announcement: `Pasted ${remapped.length} element${remapped.length === 1 ? '' : 's'}`,
        });
      }

      case 'zOrder': {
        if (state.selectedIds.length === 0) return state;
        const ids = new Set(expandForMove(state.elements, state.selectedIds));
        const moved = state.elements.filter((el) => ids.has(el.id));
        const rest = state.elements.filter((el) => !ids.has(el.id));
        const elements = action.dir === 'front' ? [...rest, ...moved] : [...moved, ...rest];
        // Z-order is array order, so no element object changed and history needs
        // an explicit entry.
        const changedIds = [...ids];
        return {
          ...state,
          elements,
          history: pushEntry(state.history, {
            elements: state.elements,
            selectedIds: state.selectedIds,
            changedIds,
          }),
          lastChange: { ids: changedIds, origin: 'local', seq: state.lastChange.seq + 1 },
          announcement: action.dir === 'front' ? 'Brought to front' : 'Sent to back',
        };
      }

      case 'undo': {
        const entry = state.history.past[state.history.past.length - 1];
        if (!entry) return { ...state, announcement: 'Nothing to undo' };
        const meta = metaOf(ctx);
        const current = state.elements;
        const restored = restoreChanged(current, entry, meta);
        const elements = normalized(ctx, restored, entry.changedIds, meta);
        return {
          ...state,
          elements,
          selectedIds: entry.selectedIds.filter((id) => elements.some((el) => el.id === id && !el.isDeleted)),
          editingTextId: null,
          interaction: null,
          history: {
            past: state.history.past.slice(0, -1),
            future: [...state.history.future, { elements: current, selectedIds: state.selectedIds, changedIds: entry.changedIds }],
            pending: null,
          },
          lastChange: { ids: entry.changedIds, origin: 'local', seq: state.lastChange.seq + 1 },
          announcement: 'Undo',
        };
      }

      case 'redo': {
        const entry = state.history.future[state.history.future.length - 1];
        if (!entry) return { ...state, announcement: 'Nothing to redo' };
        const meta = metaOf(ctx);
        const current = state.elements;
        const restored = restoreChanged(current, entry, meta);
        const elements = normalized(ctx, restored, entry.changedIds, meta);
        return {
          ...state,
          elements,
          selectedIds: entry.selectedIds.filter((id) => elements.some((el) => el.id === id && !el.isDeleted)),
          editingTextId: null,
          interaction: null,
          history: {
            past: [...state.history.past, { elements: current, selectedIds: state.selectedIds, changedIds: entry.changedIds }],
            future: state.history.future.slice(0, -1),
            pending: null,
          },
          lastChange: { ids: entry.changedIds, origin: 'local', seq: state.lastChange.seq + 1 },
          announcement: 'Redo',
        };
      }

      case 'applyRemote': {
        // Remote edits never enter the local undo stack and never clear redo.
        const meta = metaOf(ctx);
        const map = byId(state.elements);
        const changed: string[] = [];
        let out = state.elements;
        const appended: SlateElement[] = [];
        for (const incoming of action.elements) {
          const existing = map.get(incoming.id);
          if (!existing) {
            appended.push(Object.freeze(incoming));
            changed.push(incoming.id);
            continue;
          }
          if (!winsOver(incoming, existing)) continue;
          if (out === state.elements) out = state.elements.slice();
          (out as SlateElement[])[out.indexOf(existing)] = Object.freeze(incoming);
          changed.push(incoming.id);
        }
        if (changed.length === 0) return state;
        const merged = appended.length ? [...out, ...appended] : out;
        const elements = normalized(ctx, merged, changed, meta, false);
        return {
          ...state,
          elements,
          selectedIds: state.selectedIds.filter((id) => elements.some((el) => el.id === id && !el.isDeleted)),
          lastChange: { ids: changed, origin: 'remote', seq: state.lastChange.seq + 1 },
        };
      }

      case 'startEditText': {
        const el = state.elements.find((e) => e.id === action.id);
        if (!el || !isText(el)) return state;
        return { ...state, editingTextId: action.id, history: beginGesture(state), selectedIds: [action.id] };
      }

      case 'editTextValue': {
        const el = state.elements.find((e) => e.id === action.id);
        if (!el || !isText(el)) return state;
        const meta = metaOf(ctx);
        const patched = updateElement(el, { text: action.text } as Partial<TextElement>, meta);
        if (patched === el) return state;
        const dirty = [el.id];
        if (el.containerId) dirty.push(el.containerId);
        const elements = normalized(ctx, replaceElements(state.elements, new Map([[el.id, patched]])), dirty, meta);
        return markLocal(state, { ...state, elements });
      }

      case 'endEditText': {
        const id = state.editingTextId;
        if (!id) return state;
        const el = state.elements.find((e) => e.id === id);
        const meta = metaOf(ctx);
        let elements = state.elements;
        let selectedIds = state.selectedIds;
        if (el && isText(el) && el.text.trim() === '') {
          // An empty label is not worth keeping.
          elements = normalized(
            ctx,
            replaceElements(state.elements, new Map([[id, updateElement(el, { isDeleted: true } as Partial<SlateElement>, meta)]])),
            [id],
            meta,
          );
          selectedIds = el.containerId ? [el.containerId] : [];
        }
        const next = commitGesture({ ...state, elements, selectedIds, editingTextId: null });
        return markLocal(state, next);
      }

      case 'doubleClick': {
        const meta = metaOf(ctx);
        const hit = pickElement(liveElements(state.elements), action.world, ctx.measureText, state.view.zoom);
        if (hit && isText(hit)) {
          return { ...state, editingTextId: hit.id, selectedIds: [hit.id], history: beginGesture(state) };
        }
        if (hit && isBox(hit)) {
          const existing = boundTextMap(state.elements).get(hit.id);
          if (existing) {
            return { ...state, editingTextId: existing.id, selectedIds: [existing.id], history: beginGesture(state) };
          }
          const label = createText({ x: hit.x, y: hit.y }, '', state.style, meta, {
            containerId: hit.id,
            width: Math.max(16, hit.width - 16),
          });
          const elements = normalized(ctx, [...state.elements, label], [label.id, hit.id], meta);
          return {
            ...state,
            elements,
            editingTextId: label.id,
            selectedIds: [label.id],
            history: beginGesture(state),
          };
        }
        const text = createText(action.world, '', state.style, meta);
        const elements = [...state.elements, text];
        return {
          ...state,
          elements,
          editingTextId: text.id,
          selectedIds: [text.id],
          tool: 'select',
          history: beginGesture(state),
        };
      }

      case 'pointerDown':
        return pointerDown(ctx, state, action);

      case 'pointerMove':
        return pointerMove(ctx, state, action);

      case 'pointerUp':
        return pointerUp(ctx, state, action);

      case 'pointerCancel': {
        const pending = state.history.pending;
        if (!pending) return { ...state, interaction: null, marquee: null, hoverBindTargetId: null };
        return {
          ...state,
          elements: pending.elements,
          selectedIds: pending.selectedIds,
          interaction: null,
          marquee: null,
          hoverBindTargetId: null,
          history: { ...state.history, pending: null },
        };
      }
    }
  };
}

/** Last-write-wins: version, then timestamp, then client id as a stable tiebreak. */
export function winsOver(incoming: SlateElement, existing: SlateElement): boolean {
  if (incoming.version !== existing.version) return incoming.version > existing.version;
  if (incoming.updatedAt !== existing.updatedAt) return incoming.updatedAt > existing.updatedAt;
  return incoming.updatedBy > existing.updatedBy;
}

function remapReferences(el: SlateElement, idMap: ReadonlyMap<string, string>): SlateElement {
  if (isText(el) && el.containerId) {
    const next = idMap.get(el.containerId);
    if (next) return Object.freeze({ ...el, containerId: next });
    return Object.freeze({ ...el, containerId: null });
  }
  if (isArrow(el)) {
    const start = el.startBinding && idMap.get(el.startBinding.elementId);
    const end = el.endBinding && idMap.get(el.endBinding.elementId);
    return Object.freeze({
      ...el,
      startBinding: start ? { ...el.startBinding!, elementId: start } : null,
      endBinding: end ? { ...el.endBinding!, elementId: end } : null,
    });
  }
  return el;
}

function cloneSelection(state: EditorState, meta: EditMeta, offset: number): SlateElement[] {
  const ids = new Set(expandForMove(state.elements, state.selectedIds));
  const source = state.elements.filter((el) => ids.has(el.id) && !el.isDeleted);
  const idMap = new Map<string, string>();
  const copies = source.map((el) => duplicateElement(el, { dx: offset, dy: offset }, meta, idMap));
  return copies.map((el) => remapReferences(el, idMap));
}

function pointerDown(
  ctx: ReducerContext,
  state: EditorState,
  action: { world: Vec; shift: boolean; alt: boolean },
): EditorState {
  const meta = metaOf(ctx);
  const world = action.world;
  const live = liveElements(state.elements);

  if (state.tool === 'pan') {
    return {
      ...state,
      interaction: {
        kind: 'pan',
        startPointer: world,
        startScroll: { x: state.view.scrollX, y: state.view.scrollY },
      },
    };
  }

  if (state.tool === 'eraser') {
    return eraseAt(ctx, { ...state, interaction: { kind: 'erase' }, history: beginGesture(state) }, world);
  }

  if (state.tool === 'text') {
    const text = createText(world, '', state.style, meta);
    return {
      ...state,
      elements: [...state.elements, text],
      selectedIds: [text.id],
      editingTextId: text.id,
      tool: state.lockTool ? 'text' : 'select',
      history: beginGesture(state),
    };
  }

  if (state.tool === 'rect' || state.tool === 'ellipse' || state.tool === 'diamond') {
    const el = createBox(state.tool, { x: world.x, y: world.y, width: 0, height: 0 }, state.style, meta);
    return {
      ...state,
      elements: [...state.elements, el],
      selectedIds: [el.id],
      interaction: { kind: 'draw', id: el.id, origin: world },
      history: beginGesture(state),
    };
  }

  if (state.tool === 'arrow' || state.tool === 'line') {
    const target = pickBindTarget(live, world, ctx.measureText, null);
    const arrow = createArrow(
      world,
      [
        [0, 0],
        [0, 0],
      ],
      state.style,
      meta,
      { endArrowhead: state.tool === 'arrow' },
    );
    const bound: ArrowElement = target
      ? Object.freeze({ ...arrow, startBinding: { elementId: target.id, gap: DEFAULT_BINDING_GAP } })
      : arrow;
    return {
      ...state,
      elements: [...state.elements, bound],
      selectedIds: [bound.id],
      interaction: { kind: 'arrow', id: bound.id, origin: world },
      hoverBindTargetId: target?.id ?? null,
      history: beginGesture(state),
    };
  }

  if (state.tool === 'freedraw') {
    const el = createFreedraw(world, [[0, 0]], state.style, meta);
    return {
      ...state,
      elements: [...state.elements, el],
      selectedIds: [el.id],
      interaction: { kind: 'freedraw', id: el.id },
      history: beginGesture(state),
    };
  }

  // Select tool.
  if (state.selectedIds.length === 1) {
    const el = state.elements.find((e) => e.id === state.selectedIds[0]);
    if (el && !el.isDeleted) {
      const handle = pickHandle(el, world, state.view.zoom);
      if (handle?.kind === 'resize') {
        return { ...state, interaction: { kind: 'resize', id: el.id, handle: handle.handle }, history: beginGesture(state) };
      }
      if (handle?.kind === 'rotate') {
        return {
          ...state,
          interaction: { kind: 'rotate', id: el.id, grabOffset: rotationGrabOffset(el, world) },
          history: beginGesture(state),
        };
      }
      if (handle?.kind === 'point') {
        return { ...state, interaction: { kind: 'point', id: el.id, index: handle.index }, history: beginGesture(state) };
      }
    }
  }

  const hit = pickElement(live, world, ctx.measureText, state.view.zoom);
  if (!hit) {
    return {
      ...state,
      selectedIds: action.shift ? state.selectedIds : [],
      interaction: { kind: 'marquee', origin: world, base: action.shift ? state.selectedIds : [] },
      marquee: { x: world.x, y: world.y, width: 0, height: 0 },
    };
  }

  // Clicking a container's label selects the container: labels are not
  // independently draggable.
  const target = isText(hit) && hit.containerId ? state.elements.find((e) => e.id === hit.containerId) ?? hit : hit;

  let selectedIds = state.selectedIds;
  if (action.shift) {
    selectedIds = state.selectedIds.includes(target.id)
      ? state.selectedIds.filter((id) => id !== target.id)
      : [...state.selectedIds, target.id];
  } else if (!state.selectedIds.includes(target.id)) {
    selectedIds = [target.id];
  }
  if (selectedIds.length === 0) return { ...state, selectedIds };

  let working: EditorState = { ...state, selectedIds, history: beginGesture(state) };

  if (action.alt) {
    // Alt-drag duplicates: copy first, then drag the copies.
    const copies = cloneSelection(working, meta, 0);
    const elements = [...working.elements, ...copies];
    working = {
      ...working,
      elements,
      selectedIds: copies.filter((c) => !(isText(c) && c.containerId)).map((c) => c.id),
    };
  }

  const moveIds = expandForMove(working.elements, working.selectedIds);
  const origins = new Map<string, { x: number; y: number }>();
  for (const id of moveIds) {
    const el = working.elements.find((e) => e.id === id);
    if (el) origins.set(id, { x: el.x, y: el.y });
  }
  return {
    ...working,
    interaction: { kind: 'move', startPointer: world, ids: moveIds, origins, moved: false },
    announcement: describeSelection(working),
  };
}

function eraseAt(ctx: ReducerContext, state: EditorState, world: Vec): EditorState {
  const meta = metaOf(ctx);
  const hit = pickElement(liveElements(state.elements), world, ctx.measureText, state.view.zoom);
  if (!hit) return state;
  const ids = expandForMove(state.elements, [hit.id]);
  const patches = new Map<string, SlateElement>();
  for (const id of ids) {
    const el = state.elements.find((e) => e.id === id);
    if (el && !el.isDeleted) patches.set(id, updateElement(el, { isDeleted: true } as Partial<SlateElement>, meta));
  }
  if (patches.size === 0) return state;
  const elements = normalized(ctx, replaceElements(state.elements, patches), ids, meta);
  return markLocal(state, { ...state, elements });
}

function pointerMove(
  ctx: ReducerContext,
  state: EditorState,
  action: { world: Vec; shift: boolean },
): EditorState {
  const it = state.interaction;
  if (!it) return state;
  const meta = metaOf(ctx);
  const world = action.world;

  switch (it.kind) {
    case 'pan': {
      const dx = world.x - it.startPointer.x;
      const dy = world.y - it.startPointer.y;
      return {
        ...state,
        view: { ...state.view, scrollX: it.startScroll.x - dx, scrollY: it.startScroll.y - dy },
      };
    }

    case 'erase':
      return eraseAt(ctx, state, world);

    case 'draw': {
      const el = state.elements.find((e) => e.id === it.id);
      if (!el) return state;
      let rect = rectFromPoints(it.origin, world);
      if (action.shift) {
        const size = Math.max(rect.width, rect.height);
        rect = {
          x: world.x < it.origin.x ? it.origin.x - size : it.origin.x,
          y: world.y < it.origin.y ? it.origin.y - size : it.origin.y,
          width: size,
          height: size,
        };
      }
      const patched = updateElement(el, rect as Partial<SlateElement>, meta);
      return markLocal(state, { ...state, elements: replaceElements(state.elements, new Map([[el.id, patched]])) });
    }

    case 'arrow': {
      const el = state.elements.find((e) => e.id === it.id);
      if (!el || !isArrow(el)) return state;
      const local = toLocal(world, el);
      const points: Point[] = [el.points[0]!, [local.x, local.y]];
      const target = pickBindTarget(liveElements(state.elements), world, ctx.measureText, el.id);
      const patched = updateElement(el, { points } as Partial<ArrowElement>, meta);
      const elements = normalized(
        ctx,
        replaceElements(state.elements, new Map([[el.id, patched]])),
        [el.id],
        meta,
      );
      return markLocal(state, { ...state, elements, hoverBindTargetId: target?.id ?? null });
    }

    case 'freedraw': {
      const el = state.elements.find((e) => e.id === it.id);
      if (!el || !isLinear(el)) return state;
      const local = toLocal(world, el);
      const last = el.points[el.points.length - 1]!;
      if (Math.hypot(local.x - last[0], local.y - last[1]) < 1.2) return state;
      const points: Point[] = [...el.points, [local.x, local.y]];
      const patched = updateElement(el, { points } as Partial<SlateElement>, meta);
      return markLocal(state, { ...state, elements: replaceElements(state.elements, new Map([[el.id, patched]])) });
    }

    case 'move': {
      const dx = world.x - it.startPointer.x;
      const dy = world.y - it.startPointer.y;
      const moved = it.moved || Math.hypot(dx, dy) > DRAG_THRESHOLD;
      if (!moved) return state;
      const patches = new Map<string, SlateElement>();
      for (const id of it.ids) {
        const el = state.elements.find((e) => e.id === id);
        const origin = it.origins.get(id);
        if (!el || !origin) continue;
        patches.set(id, updateElement(el, { x: origin.x + dx, y: origin.y + dy } as Partial<SlateElement>, meta));
      }
      const elements = normalized(ctx, replaceElements(state.elements, patches), it.ids, meta);
      return markLocal(state, { ...state, elements, interaction: { ...it, moved } });
    }

    case 'resize': {
      const el = state.elements.find((e) => e.id === it.id);
      if (!el) return state;
      const result = resizeElement(el, it.handle, world, { keepAspect: action.shift });
      const { handle, ...patch } = result;
      const patches = new Map<string, SlateElement>([[el.id, updateElement(el, patch as Partial<SlateElement>, meta)]]);
      const dirty = [el.id];
      const label = boundTextMap(state.elements).get(el.id);
      if (label) dirty.push(label.id);
      const elements = normalized(ctx, replaceElements(state.elements, patches), dirty, meta);
      return markLocal(state, { ...state, elements, interaction: { ...it, handle } });
    }

    case 'rotate': {
      const el = state.elements.find((e) => e.id === it.id);
      if (!el) return state;
      const angle = rotateElement(el, world, it.grabOffset, action.shift);
      const patches = new Map<string, SlateElement>([[el.id, updateElement(el, { angle } as Partial<SlateElement>, meta)]]);
      const dirty = [el.id];
      const label = boundTextMap(state.elements).get(el.id);
      if (label) dirty.push(label.id);
      const elements = normalized(ctx, replaceElements(state.elements, patches), dirty, meta);
      return markLocal(state, { ...state, elements });
    }

    case 'point': {
      const el = state.elements.find((e) => e.id === it.id);
      if (!el) return state;
      const points = moveLinearPoint(el, it.index, world);
      if (!points) return state;
      const target = pickBindTarget(liveElements(state.elements), world, ctx.measureText, el.id);
      const patches = new Map<string, SlateElement>([[el.id, updateElement(el, { points } as Partial<SlateElement>, meta)]]);
      const elements = normalized(ctx, replaceElements(state.elements, patches), [el.id], meta);
      return markLocal(state, { ...state, elements, hoverBindTargetId: target?.id ?? null });
    }

    case 'marquee': {
      const rect = rectFromPoints(it.origin, world);
      const inside = elementsInRect(liveElements(state.elements), rect)
        .filter((el) => !(isText(el) && el.containerId))
        .map((el) => el.id);
      const merged = [...new Set([...it.base, ...inside])];
      return { ...state, marquee: rect, selectedIds: merged };
    }
  }
}

function pointerUp(ctx: ReducerContext, state: EditorState, action: { world: Vec }): EditorState {
  const it = state.interaction;
  if (!it) return state;
  const meta = metaOf(ctx);
  let next: EditorState = { ...state, interaction: null, marquee: null, hoverBindTargetId: null };

  switch (it.kind) {
    case 'pan':
      return next;

    case 'erase':
      return commitGesture(next);

    case 'draw': {
      const el = next.elements.find((e) => e.id === it.id);
      if (!el) return commitGesture(next);
      let elements = next.elements;
      if (el.width < DRAG_THRESHOLD && el.height < DRAG_THRESHOLD) {
        // A click rather than a drag: place a default-sized shape centred there.
        const patched = updateElement(
          el,
          {
            x: it.origin.x - CLICK_PLACE_SIZE.width / 2,
            y: it.origin.y - CLICK_PLACE_SIZE.height / 2,
            width: CLICK_PLACE_SIZE.width,
            height: CLICK_PLACE_SIZE.height,
          } as Partial<SlateElement>,
          meta,
        );
        elements = replaceElements(elements, new Map([[el.id, patched]]));
      }
      next = {
        ...next,
        elements: normalized(ctx, elements, [el.id], meta),
        tool: next.lockTool ? next.tool : 'select',
        announcement: `Added ${el.type}`,
      };
      return commitGesture(markLocal(state, next));
    }

    case 'freedraw': {
      const el = next.elements.find((e) => e.id === it.id);
      if (!el || !isLinear(el)) return commitGesture(next);
      if (el.points.length < 2) {
        // A stray click leaves nothing behind.
        return commitGesture({
          ...next,
          elements: next.elements.filter((e) => e.id !== el.id),
          selectedIds: [],
        });
      }
      next = {
        ...next,
        elements: normalized(ctx, next.elements, [el.id], meta),
        tool: next.lockTool ? next.tool : 'select',
        announcement: 'Added drawing',
      };
      return commitGesture(markLocal(state, next));
    }

    case 'arrow': {
      const el = next.elements.find((e) => e.id === it.id);
      if (!el || !isArrow(el)) return commitGesture(next);
      const pts = el.points;
      const span = Math.hypot(
        pts[pts.length - 1]![0] - pts[0]![0],
        pts[pts.length - 1]![1] - pts[0]![1],
      );
      if (span < 6) {
        return commitGesture({
          ...next,
          elements: next.elements.filter((e) => e.id !== el.id),
          selectedIds: [],
        });
      }
      const target = pickBindTarget(liveElements(next.elements), action.world, ctx.measureText, el.id);
      // Self-loops are refused rather than rendered badly.
      const endBinding =
        target && target.id !== el.startBinding?.elementId
          ? { elementId: target.id, gap: DEFAULT_BINDING_GAP }
          : null;
      const patched = updateElement(el, { endBinding } as Partial<ArrowElement>, meta);
      const elements = normalized(
        ctx,
        replaceElements(next.elements, new Map([[el.id, patched]])),
        [el.id],
        meta,
      );
      const connected = el.startBinding && endBinding;
      next = {
        ...next,
        elements,
        tool: next.lockTool ? next.tool : 'select',
        announcement: connected ? 'Connected two shapes' : 'Added arrow',
      };
      return commitGesture(markLocal(state, next));
    }

    case 'point': {
      const el = next.elements.find((e) => e.id === it.id);
      if (!el || !isArrow(el)) return commitGesture(next);
      const target = pickBindTarget(liveElements(next.elements), action.world, ctx.measureText, el.id);
      const isStart = it.index === 0;
      const otherId = isStart ? el.endBinding?.elementId : el.startBinding?.elementId;
      // Dropping an endpoint on a shape connects it; dropping it on empty canvas
      // disconnects it.
      const binding = target && target.id !== otherId ? { elementId: target.id, gap: DEFAULT_BINDING_GAP } : null;
      const patch = isStart ? { startBinding: binding } : { endBinding: binding };
      const patched = updateElement(el, patch as Partial<ArrowElement>, meta);
      const elements = normalized(
        ctx,
        replaceElements(next.elements, new Map([[el.id, patched]])),
        [el.id],
        meta,
      );
      return commitGesture(markLocal(state, { ...next, elements }));
    }

    case 'move':
    case 'resize':
    case 'rotate':
      return commitGesture(next);

    case 'marquee':
      return { ...next, announcement: describeSelection(next) };
  }
}

/** Serialize the editor's scene back into a persistable board. */
export function toBoard(state: EditorState, previous?: Board | null): Board {
  return {
    id: state.boardId ?? previous?.id ?? '',
    name: state.boardName,
    createdAt: previous?.createdAt ?? Date.now(),
    updatedAt: Date.now(),
    docVersion: DOC_VERSION,
    elements: state.elements,
    view: state.view,
    isSample: state.isSample,
  };
}
